'use client'

import { memo } from 'react'
import { Building2, Lock, ShieldCheck, type LucideIcon } from 'lucide-react'
import { useReducedMotion } from 'framer-motion'
import { EvervaultCard } from '@/components/ui/evervault-card'
import { cn } from '@/lib/utils'

type HeroChip = {
  label: string
  icon?: LucideIcon
  blink?: boolean
}

const ENTERPRISE_HERO_CHIPS: HeroChip[] = [
  { label: 'NINX Enterprise', blink: true },
  { label: 'Sovereign AI', icon: ShieldCheck },
  { label: 'Hybrid Routing', icon: Lock },
]

type FloatChip = {
  label: string
  icon: LucideIcon
  /** Percent position around the chip visual */
  x: number
  y: number
}

const FLOATING_CHIPS: FloatChip[] = [
  { label: '"Review compliance logs"', icon: ShieldCheck, x: 14, y: 18 },
  { label: '"Route sensitive locally"', icon: Lock, x: 8, y: 42 },
  { label: '"Deploy team agents"', icon: Building2, x: 16, y: 72 },
  { label: '"Audit workflow run"', icon: ShieldCheck, x: 86, y: 20 },
  { label: '"Air-gapped inference"', icon: Lock, x: 92, y: 46 },
  { label: '"Policy gate passed"', icon: ShieldCheck, x: 84, y: 74 },
]

/** Animated dotted traces from chip center to floating nodes */
const CHIP_CENTER = { x: 450, y: 260 }

const TRACE_PATHS = FLOATING_CHIPS.map((chip) => {
  const endX = (chip.x / 100) * 900
  const endY = (chip.y / 100) * 520
  const midX = CHIP_CENTER.x + (endX - CHIP_CENTER.x) * 0.45
  const midY = CHIP_CENTER.y + (endY - CHIP_CENTER.y) * 0.1
  return {
    id: chip.label,
    d: `M ${CHIP_CENTER.x} ${CHIP_CENTER.y} Q ${midX} ${midY} ${endX} ${endY}`,
    endX,
    endY,
  }
})

const AnimatedCircuitTraces = memo(function AnimatedCircuitTraces() {
  const shouldReduce = useReducedMotion()

  return (
    <svg
      viewBox="0 0 900 520"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden
    >
      <defs>
        <linearGradient id="ninxl-ent-trace-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#21728d" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#21728d" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#88a1ac" stopOpacity="0.35" />
        </linearGradient>
      </defs>

      {TRACE_PATHS.map((trace, index) => (
        <g key={trace.id}>
          <path
            d={trace.d}
            fill="none"
            stroke="url(#ninxl-ent-trace-grad)"
            strokeWidth="1.5"
            strokeDasharray="3 9"
            className={shouldReduce ? undefined : 'ninxl-enterprise-trace'}
            style={{ animationDelay: `${index * 0.35}s` }}
          />
          <rect
            x={trace.endX - 4}
            y={trace.endY - 4}
            width="8"
            height="8"
            fill="#21728d"
            fillOpacity="0.35"
          />
          {!shouldReduce ? (
            <circle r="3" fill="#21728d" opacity="0.9">
              <animateMotion
                dur={`${2.8 + index * 0.2}s`}
                repeatCount="indefinite"
                path={trace.d}
                calcMode="linear"
              />
            </circle>
          ) : null}
        </g>
      ))}
    </svg>
  )
})

/** 8-bit hero pills: /ninx intro chips in pixel style */
export const Ninxl8BitHeroChips = memo(function Ninxl8BitHeroChips({
  chips = ENTERPRISE_HERO_CHIPS,
  className,
}: {
  chips?: HeroChip[]
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-2 md:gap-3', className)}>
      {chips.map((chip) => (
        <span key={chip.label} className="ninxl-8bit-chip ninxl-8bit-float-up">
          {chip.blink ? <span className="ninxl-8bit-blink">●</span> : null}
          {chip.icon ? <chip.icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
          {chip.label}
        </span>
      ))}
    </div>
  )
})

const PinRow = ({ vertical }: { vertical?: boolean }) => (
  <div className={cn('flex gap-1.5', vertical ? 'flex-col' : 'flex-row')}>
    {Array.from({ length: 4 }).map((_, index) => (
      <div
        key={index}
        className={cn(
          'bg-[color:var(--ninxl-8bit-dim)]',
          vertical ? 'h-3 w-1.5' : 'h-1.5 w-3',
        )}
      />
    ))}
  </div>
)

const CornerDots = ({ className }: { className: string }) => (
  <div className={cn('absolute flex gap-1', className)}>
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="h-1.5 w-1.5 bg-[color:var(--ninxl-8bit-dim)]" />
    ))}
  </div>
)

/** 8-bit processor chip: full package is the Evervault hover surface */
export const Ninxl8BitProcessorChip = memo(function Ninxl8BitProcessorChip({
  className,
  label = 'NINX',
  sublabel = 'ENTERPRISE CORE',
}: {
  className?: string
  label?: string
  sublabel?: string
}) {
  return (
    <div className={cn('relative z-30 mx-auto h-52 w-52 md:h-64 md:w-64', className)}>
      <div className="ninxl-pixel-border ninxl-theme-bg-frame relative h-full w-full overflow-hidden">
        {/* baby: Cipher field covers the entire chip body, not only the center die */}
        <EvervaultCard
          text={label}
          size="md"
          className="h-full w-full"
          roundedClassName="rounded-none"
        >
          <div className="text-center">
            <div className="font-mono text-[7px] tracking-[0.14em] text-[color:var(--ninxl-theme-fg-muted,#6b7280)] opacity-80 md:text-[8px]">
              {sublabel}
            </div>
          </div>
        </EvervaultCard>

        <CornerDots className="pointer-events-none left-4 top-4 z-20" />
        <CornerDots className="pointer-events-none right-4 top-4 z-20" />
        <CornerDots className="pointer-events-none bottom-4 left-4 z-20" />
        <CornerDots className="pointer-events-none bottom-4 right-4 z-20" />
      </div>

      <div className="pointer-events-none absolute -left-1.5 top-1/2 z-20 -translate-y-1/2">
        <PinRow vertical />
      </div>
      <div className="pointer-events-none absolute -right-1.5 top-1/2 z-20 -translate-y-1/2">
        <PinRow vertical />
      </div>
      <div className="pointer-events-none absolute -top-1.5 left-1/2 z-20 -translate-x-1/2">
        <PinRow />
      </div>
      <div className="pointer-events-none absolute -bottom-1.5 left-1/2 z-20 -translate-x-1/2">
        <PinRow />
      </div>
    </div>
  )
})

/** Full enterprise hero chip stack */
export const NinxlEnterpriseHeroChipVisual = memo(function NinxlEnterpriseHeroChipVisual({
  className,
}: {
  className?: string
}) {
  return (
    <div className={cn('relative mx-auto min-h-[440px] w-full max-w-4xl md:min-h-[520px]', className)}>
      <AnimatedCircuitTraces />

      <div className="relative flex min-h-[440px] items-center justify-center md:min-h-[520px]">
        <Ninxl8BitProcessorChip />

        {FLOATING_CHIPS.map((chip) => (
          <div
            key={chip.label}
            className="ninxl-8bit-float-chip absolute z-20 hidden items-center gap-2 md:flex"
            style={{ left: `${chip.x}%`, top: `${chip.y}%`, transform: 'translate(-50%, -50%)' }}
          >
            <chip.icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{chip.label}</span>
          </div>
        ))}
      </div>

      <div className="ninxl-8bit-label ninxl-theme-text-muted relative z-10 mt-2 text-center">
        PRIVACY GATE ACTIVE · LOCAL PERIMETER SECURED
      </div>
    </div>
  )
})